import { createContext, useContext } from "react";
import type { AmpFileUpdateInput, AmpLibraryFileFragment } from "src/graphql";

/** A rename sent for a file still at `versionId`. */
export type PendingName = Pick<AmpLibraryFileFragment, "versionId"> & {
  name: string;
};

export type CanvasRename = {
  /** The node whose name is a field, if any. */
  nodeId: string | null;
  start: (nodeId: string) => void;
  stop: (nodeId: string) => void;
  /** Renames on their way to the server, by file id. */
  pendingNames: ReadonlyMap<string, PendingName>;
  setPendingName: (fileId: string, pending: PendingName | null) => void;
};

export const CanvasRenameContext = createContext<CanvasRename>({
  nodeId: null,
  start: () => {},
  stop: () => {},
  pendingNames: new Map(),
  setPendingName: () => {},
});

export const useCanvasRename = () => useContext(CanvasRenameContext);

/** Docs and uploads only: renaming an ad or an email would write a new version of its content. */
export function renameInput(
  file: AmpLibraryFileFragment,
  name: string,
): AmpFileUpdateInput | undefined {
  const base = { id: file.id, baseVersionId: file.versionId };
  if (file.type === "doc" && file.markdown)
    return {
      ...base,
      ampFileMarkdown: { name, content: { markdown: file.markdown.markdown } },
    };
  if (file.type === "upload")
    return { ...base, ampFileUpload: { name, content: { file: null } } };
  return undefined;
}

export const canRenameOnCanvas = (file: AmpLibraryFileFragment) =>
  renameInput(file, file.name ?? "") !== undefined;

/** A sent rename is pending until the card has the version it wrote. */
export const isRenamePending = (
  file: AmpLibraryFileFragment,
  pending: PendingName | undefined,
): pending is PendingName => pending?.versionId === file.versionId;

export const shownName = (
  file: AmpLibraryFileFragment,
  pending: PendingName | undefined,
) => (isRenamePending(file, pending) ? pending.name : file.name);
